// Canvas-based share card generator for Radio Rumore Zero.
// Produces a 1080x1080 JPEG suitable for Instagram/WhatsApp status.

function parseTrack(raw) {
  const s = (raw || "").trim();
  if (s.includes(" - ")) {
    const [left, ...rest] = s.split(" - ");
    const right = rest.join(" - ").trim();
    if (left && right) {
      return left.length <= right.length
        ? { artist: left.trim(), title: right }
        : { artist: right, title: left.trim() };
    }
  }
  return { artist: "Radio Rumore Zero", title: s };
}

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}

function fitText(ctx, text, maxWidth, maxFont, minFont) {
  let size = maxFont;
  ctx.font = `bold ${size}px "Anton", "Oswald", sans-serif`;
  while (ctx.measureText(text).width > maxWidth && size > minFont) {
    size -= 4;
    ctx.font = `bold ${size}px "Anton", "Oswald", sans-serif`;
  }
  return size;
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

export async function generateShareCard({ raw, cover }) {
  const { artist, title } = parseTrack(raw);

  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Background
  const bg = ctx.createLinearGradient(0, 0, 0, 1080);
  bg.addColorStop(0, "#1a1714");
  bg.addColorStop(1, "#0a0907");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, 1080, 1080);

  // Warm accent circles
  ctx.fillStyle = "rgba(255,107,0,0.16)";
  ctx.beginPath();
  ctx.arc(1080, 0, 520, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(225,161,64,0.10)";
  ctx.beginPath();
  ctx.arc(0, 1080, 480, 0, Math.PI * 2);
  ctx.fill();

  // Brand strip (top)
  ctx.fillStyle = "#FF6B00";
  ctx.font = "bold 42px \"Anton\", sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("RUMORE", 80, 130);
  ctx.fillStyle = "#cf2020";
  ctx.fillText("ZERO", 350, 130);
  ctx.fillStyle = "#69635c";
  ctx.font = "22px \"IBM Plex Mono\", monospace";
  ctx.fillText("· 60 · 70 · 80 · 90", 500, 130);

  // Live badge
  ctx.fillStyle = "rgba(255,107,0,0.15)";
  roundedRect(ctx, 80, 170, 220, 44, 22);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,107,0,0.55)";
  ctx.lineWidth = 2;
  roundedRect(ctx, 80, 170, 220, 44, 22);
  ctx.stroke();
  ctx.fillStyle = "#FF6B00";
  ctx.beginPath();
  ctx.arc(112, 192, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.font = "bold 20px \"IBM Plex Mono\", monospace";
  ctx.textBaseline = "middle";
  ctx.fillText("IN DIRETTA · 24/7", 130, 193);
  ctx.textBaseline = "alphabetic";

  // Cover area 600x600, centered
  const size = 600;
  const cx = (1080 - size) / 2;
  const cy = 260;

  // Cover shadow
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  roundedRect(ctx, cx + 8, cy + 12, size, size, 28);
  ctx.fill();

  if (cover) {
    try {
      const img = await loadImage(cover);
      ctx.save();
      roundedRect(ctx, cx, cy, size, size, 28);
      ctx.clip();
      ctx.drawImage(img, cx, cy, size, size);
      ctx.restore();
      // Border
      ctx.strokeStyle = "rgba(255,107,0,0.35)";
      ctx.lineWidth = 3;
      roundedRect(ctx, cx, cy, size, size, 28);
      ctx.stroke();
    } catch {
      // Fallback: styled placeholder
      ctx.fillStyle = "#1a1816";
      roundedRect(ctx, cx, cy, size, size, 28);
      ctx.fill();
      ctx.fillStyle = "#FF6B00";
      ctx.font = "bold 220px \"Anton\", sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("♫", 540, cy + size / 2);
    }
  } else {
    ctx.fillStyle = "#1a1816";
    roundedRect(ctx, cx, cy, size, size, 28);
    ctx.fill();
    ctx.fillStyle = "#FF6B00";
    ctx.font = "bold 220px \"Anton\", sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("♫", 540, cy + size / 2);
  }

  // Now playing label
  ctx.fillStyle = "#69635c";
  ctx.font = "24px \"IBM Plex Mono\", monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("STO ASCOLTANDO", 540, 920);

  // Title
  ctx.fillStyle = "#F5F2EB";
  const titleSize = fitText(ctx, title.toUpperCase(), 940, 68, 32);
  ctx.font = `bold ${titleSize}px "Anton", sans-serif`;
  ctx.fillText(title.toUpperCase(), 540, 972);

  // Artist
  ctx.fillStyle = "#E1A140";
  ctx.font = "28px \"Oswald\", sans-serif";
  ctx.fillText(artist.toUpperCase(), 540, 1010);

  // URL
  ctx.fillStyle = "#69635c";
  ctx.font = "22px \"IBM Plex Mono\", monospace";
  ctx.fillText("zero-radio-app.emergent.host", 540, 1050);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.88));
}

// ============================================================================
// Favorites list card — 1080x1080, up to 10 tracks
// ============================================================================
export async function generateFavoritesCard({ favorites = [], userName }) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Background
  const bg = ctx.createLinearGradient(0, 0, 0, 1080);
  bg.addColorStop(0, "#1a1714");
  bg.addColorStop(1, "#0a0907");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, 1080, 1080);

  // Warm accent circles
  ctx.fillStyle = "rgba(255,107,0,0.18)";
  ctx.beginPath();
  ctx.arc(1080, 0, 540, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(207,32,32,0.12)";
  ctx.beginPath();
  ctx.arc(0, 1080, 500, 0, Math.PI * 2);
  ctx.fill();

  // Brand strip (top) — bold, big, high-contrast
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  // soft glow behind the logo
  ctx.save();
  ctx.shadowColor = "rgba(255,107,0,0.55)";
  ctx.shadowBlur = 24;
  ctx.fillStyle = "#FF6B00";
  ctx.font = "900 92px \"Anton\", sans-serif";
  ctx.fillText("RUMORE", 80, 160);
  const rumoreW = ctx.measureText("RUMORE").width;
  ctx.shadowColor = "rgba(207,32,32,0.55)";
  ctx.fillStyle = "#ff2d2d";
  ctx.fillText("ZERO", 80 + rumoreW + 22, 160);
  ctx.restore();
  // decade tag line
  ctx.fillStyle = "#A39A8F";
  ctx.font = "22px \"IBM Plex Mono\", monospace";
  ctx.fillText("60 · 70 · 80 · 90 · I GRANDI SUCCESSI", 82, 196);

  // Heart badge
  ctx.fillStyle = "rgba(255,107,0,0.15)";
  roundedRect(ctx, 80, 220, 300, 48, 24);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,107,0,0.55)";
  ctx.lineWidth = 2;
  roundedRect(ctx, 80, 220, 300, 48, 24);
  ctx.stroke();
  // heart glyph (unicode)
  ctx.fillStyle = "#FF6B00";
  ctx.font = "bold 24px \"Anton\", sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText("♥", 108, 245);
  ctx.font = "bold 20px \"IBM Plex Mono\", monospace";
  ctx.fillText("I MIEI PREFERITI", 142, 245);
  ctx.textBaseline = "alphabetic";

  // Headline
  ctx.fillStyle = "#F5F2EB";
  ctx.font = "bold 72px \"Anton\", sans-serif";
  const title = (userName && userName.trim())
    ? `LA PLAYLIST DI ${userName.trim().toUpperCase().slice(0, 18)}`
    : "LE MIE CANZONI DEL CUORE";
  const titleSize = fitText(ctx, title, 920, 60, 32);
  ctx.font = `bold ${titleSize}px "Anton", sans-serif`;
  ctx.fillText(title, 80, 340);

  // Divider
  ctx.strokeStyle = "rgba(255,107,0,0.35)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(80, 370);
  ctx.lineTo(1000, 370);
  ctx.stroke();

  // Up to 10 track rows
  const items = favorites.slice(0, 10);
  const rowH = 60;
  const startY = 420;
  items.forEach((fav, i) => {
    const y = startY + i * rowH;
    const { artist, title: trackTitle } = parseTrack(fav.raw);

    // Position number
    ctx.fillStyle = "#FF6B00";
    ctx.font = "bold 32px \"Anton\", sans-serif";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.fillText(String(i + 1).padStart(2, "0"), 130, y + 20);

    // Title + artist
    ctx.textAlign = "left";
    ctx.fillStyle = "#F5F2EB";
    ctx.font = "bold 30px \"Oswald\", sans-serif";
    const titleStr = (trackTitle || fav.raw || "").toUpperCase();
    const shownTitle = truncateToWidth(ctx, titleStr, 720);
    ctx.fillText(shownTitle, 160, y + 10);

    if (artist) {
      ctx.fillStyle = "#A39A8F";
      ctx.font = "22px \"IBM Plex Mono\", monospace";
      const shownArtist = truncateToWidth(ctx, artist.toUpperCase(), 720);
      ctx.fillText(shownArtist, 160, y + 42);
    }

    // Code chip (if available)
    if (fav.code) {
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      const chipText = `*${fav.code}`;
      ctx.font = "bold 20px \"IBM Plex Mono\", monospace";
      const w = Math.max(80, ctx.measureText(chipText).width + 24);
      const chipX = 1000 - w;
      const chipY = y + 2;
      ctx.fillStyle = "rgba(255,107,0,0.14)";
      roundedRect(ctx, chipX, chipY, w, 36, 8);
      ctx.fill();
      ctx.strokeStyle = "rgba(255,107,0,0.5)";
      ctx.lineWidth = 1.5;
      roundedRect(ctx, chipX, chipY, w, 36, 8);
      ctx.stroke();
      ctx.fillStyle = "#FF6B00";
      ctx.textAlign = "center";
      ctx.fillText(chipText, chipX + w / 2, chipY + 20);
    }
  });

  // "+N altri" if more
  if (favorites.length > items.length) {
    const extraY = startY + items.length * rowH + 14;
    ctx.fillStyle = "#69635c";
    ctx.font = "italic 24px \"IBM Plex Mono\", monospace";
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";
    ctx.fillText(`+ ${favorites.length - items.length} altri brani…`, 160, extraY);
  }

  // Footer CTA
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#69635c";
  ctx.font = "22px \"IBM Plex Mono\", monospace";
  ctx.fillText("ASCOLTA ANCHE TU · 24/7", 540, 1010);
  ctx.fillStyle = "#FF6B00";
  ctx.font = "bold 28px \"IBM Plex Mono\", monospace";
  ctx.fillText("zero-radio-app.emergent.host", 540, 1050);

  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
}

function truncateToWidth(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 2 && ctx.measureText(t + "…").width > maxWidth) {
    t = t.slice(0, -1);
  }
  return t + "…";
}
